/* 3D Light Box Fundraiser — page interactions.

   Handles:
   - Countdown (client-side ISO-8601 delta; edge cache-safe)
   - Goal meter polling + stat strip sync
   - Cart attribute setter (tags cart as in-fundraiser-context on page load)
   - AJAX add-to-cart for fundraiser products
   - Slide-in cart drawer with line items, totals, checkout
   - Floating cart pill (in-page mini cart trigger)
   - Supporters wall fetch + poll
   - Social share row (Web Share API + clipboard fallback)

   Read endpoints (goal meter, supporters wall) call the Supabase function
   directly via CORS rather than going through a Shopify App Proxy. The data
   returned is the same as what's already public on the storefront, so no PII
   exposure. This sidesteps Shopify's single-store constraint on non-Plus
   custom-distribution apps and works the same way on every fundraiser store.
*/

(function () {
  'use strict';

  var FUNDRAISER_API = 'https://ubcmgtgmwaigwcliqrru.supabase.co/functions/v1/sk-fundraiser';

  // ---------- utilities ----------
  function formatMoneyFromCents(cents) {
    var dollars = (cents / 100).toFixed(2);
    var parts = dollars.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + whole + '.' + parts[1];
  }

  function formatMoneyFromDollars(amount) {
    var parts = amount.toFixed(2).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + whole + '.' + parts[1];
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function $(selector, root) { return (root || document).querySelector(selector); }
  function $$(selector, root) { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); }

  // ---------- countdown ----------
  function startCountdown(root) {
    var endIso = root.getAttribute('data-end');
    if (!endIso) return;
    var endTs = Date.parse(endIso);
    if (Number.isNaN(endTs)) return;

    var daysEl = $('[data-fc-days]', root);
    var hoursEl = $('[data-fc-hours]', root);
    var minutesEl = $('[data-fc-minutes]', root);
    var secondsEl = $('[data-fc-seconds]', root);
    var endedEl = $('[data-fc-ended]', root);
    var grid = $('.fundraiser-countdown__grid, .fr-impact__countdown', root);

    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function tick() {
      var diff = endTs - Date.now();
      if (diff <= 0) {
        if (daysEl) daysEl.textContent = '0';
        if (hoursEl) hoursEl.textContent = '00';
        if (minutesEl) minutesEl.textContent = '00';
        if (secondsEl) secondsEl.textContent = '00';
        if (endedEl) endedEl.hidden = false;
        if (grid) grid.setAttribute('aria-hidden', 'true');
        return false;
      }
      var totalSeconds = Math.floor(diff / 1000);
      var days = Math.floor(totalSeconds / 86400);
      var hours = Math.floor((totalSeconds % 86400) / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;
      if (daysEl) daysEl.textContent = String(days);
      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minutesEl) minutesEl.textContent = pad(minutes);
      if (secondsEl) secondsEl.textContent = pad(seconds);
      // Also update any stat-strip "days left" mirrors
      $$('[data-stat-days]').forEach(function (el) { el.textContent = String(days); });
      return true;
    }

    if (!tick()) return;
    setInterval(function () {
      if (!tick()) {
        // tick() returns false at expiry; we still ran one final write above.
      }
    }, 1000);
  }

  $$('[data-fundraiser-countdown]').forEach(startCountdown);

  // ---------- goal meter live refresh ----------
  function startGoalRefresh(root) {
    var slug = root.getAttribute('data-slug');
    var goal = parseFloat(root.getAttribute('data-goal') || '0');
    var contributionPerUnit = parseFloat(root.getAttribute('data-contribution-per-unit') || '0');
    if (!slug || !(goal > 0)) return;

    var raisedEl = $('[data-fg-raised]', root);
    var fillEl = $('[data-fg-fill]', root);
    var barEl = $('[data-fg-bar]', root);
    var percentEl = $('[data-fg-percent]', root);
    var unitsEl = $('[data-fg-units]', root);

    function applyData(data) {
      var units = typeof data.units_sold === 'number' ? data.units_sold : 0;
      var raised = contributionPerUnit > 0
        ? units * contributionPerUnit
        : data.raised_amount;
      if (typeof raised !== 'number') return;
      var percent = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
      if (raisedEl) raisedEl.textContent = formatMoneyFromDollars(raised);
      if (fillEl) fillEl.style.width = percent + '%';
      if (barEl) barEl.setAttribute('aria-valuenow', String(percent));
      if (percentEl) percentEl.textContent = String(percent);
      if (unitsEl) unitsEl.textContent = String(units);
      // Mirror into stat strip
      $$('[data-stat-raised]').forEach(function (el) { el.textContent = formatMoneyFromDollars(raised); });
      $$('[data-stat-percent]').forEach(function (el) { el.textContent = String(percent); });
    }

    async function refresh() {
      try {
        var response = await fetch(FUNDRAISER_API + '/progress/' + encodeURIComponent(slug), {
          headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) return;
        var data = await response.json();
        applyData(data);
      } catch (err) {
        // Silent — initial render still shows last-known value.
      }
    }

    refresh();
    setInterval(refresh, 30000);
  }

  $$('[data-fundraiser-goal]').forEach(startGoalRefresh);

  // ---------- supporters wall ----------
  function startSupporters(root) {
    var slug = root.getAttribute('data-slug');
    if (!slug) return;

    var listEl = $('[data-supporters-list]', root);
    var emptyEl = $('[data-supporters-empty]', root);
    var totalEl = $('[data-supporters-total]', root);
    var moreEl = $('[data-supporters-more]', root);

    function render(supporters, total, mode) {
      if (!listEl) return;
      if (!supporters || supporters.length === 0) {
        if (emptyEl) emptyEl.hidden = false;
        listEl.innerHTML = '';
        if (totalEl) totalEl.textContent = '0';
        if (moreEl) moreEl.hidden = true;
        return;
      }
      if (emptyEl) emptyEl.hidden = true;
      var visible = supporters.slice(0, 10);
      var anonymous = mode === 'city' || mode === 'aggregate' || !supporters[0].first_name || supporters[0].first_name === 'Supporter';
      listEl.innerHTML = visible.map(function (s, i) {
        var city = escapeHtml(s.city || '');
        var units = s.units ? s.units + ' ' + (s.units === 1 ? 'item' : 'items') : '';
        var avatar = anonymous ? String(i + 1) : escapeHtml((s.first_name || 'S').slice(0, 1));
        var label;
        if (anonymous) {
          label = city ? 'Someone from ' + city : 'A supporter';
        } else {
          var name = escapeHtml(s.first_name || 'Supporter');
          label = name + (city ? ' from ' + city : '');
        }
        return '<li class="fr-supporters__item">'
          + '<span class="fr-supporters__avatar" aria-hidden="true">' + avatar + '</span>'
          + '<span class="fr-supporters__name">' + label + '</span>'
          + '<span class="fr-supporters__meta">' + units + '</span>'
          + '</li>';
      }).join('');
      if (totalEl) totalEl.textContent = String(total || supporters.length);
      var extra = (total || supporters.length) - visible.length;
      if (moreEl) {
        if (extra > 0) {
          moreEl.hidden = false;
          moreEl.textContent = '+ ' + extra + ' more ' + (extra === 1 ? 'supporter' : 'supporters');
        } else {
          moreEl.hidden = true;
        }
      }
    }

    async function refresh() {
      try {
        var response = await fetch(FUNDRAISER_API + '/supporters/' + encodeURIComponent(slug), {
          headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) {
          render([], 0);
          return;
        }
        var data = await response.json();
        render(data.supporters || [], data.total || 0, data.mode);
      } catch (err) {
        render([], 0);
      }
    }

    refresh();
    setInterval(refresh, 60000);
  }

  $$('[data-fundraiser-supporters]').forEach(startSupporters);

  // ---------- cart drawer ----------
  var drawerRoot = $('[data-fundraiser-drawer]');
  var fundraiserSlug = drawerRoot ? drawerRoot.getAttribute('data-slug') : '';
  var lastFocus = null;

  // Determine the slug for the current page (used to set cart attribute).
  // The drawer reads from the cart attribute too, but on first visit there's no attribute yet.
  function inferSlug() {
    // Prefer a slug from a product page (data-fundraiser-slug on the product section)
    var productSection = $('[data-fundraiser-product]');
    if (productSection) {
      var s = productSection.getAttribute('data-fundraiser-slug');
      if (s) return s;
    }
    // Then drawer
    if (fundraiserSlug) return fundraiserSlug;
    // Fall back to body class hint (set by other sections on listing page)
    var listing = document.body.getAttribute('data-fundraiser-slug');
    if (listing) return listing;
    return '';
  }

  async function setCartAttribute() {
    var slug = inferSlug();
    if (!slug) return;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _fundraiser_slug: slug } }),
        credentials: 'same-origin'
      });
    } catch (err) {
      // Non-fatal; the line item property is still set on each add-to-cart.
    }
  }

  function openDrawer() {
    if (!drawerRoot) return;
    lastFocus = document.activeElement;
    drawerRoot.hidden = false;
    requestAnimationFrame(function () {
      drawerRoot.setAttribute('aria-hidden', 'false');
      drawerRoot.classList.add('is-open');
    });
    document.body.classList.add('fr-drawer-open');
    var panel = $('.fr-drawer__panel', drawerRoot);
    if (panel) panel.focus();
  }

  function closeDrawer() {
    if (!drawerRoot) return;
    drawerRoot.classList.remove('is-open');
    drawerRoot.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('fr-drawer-open');
    setTimeout(function () { drawerRoot.hidden = true; }, 280);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function renderDrawer(cart) {
    if (!drawerRoot) return;
    var linesEl = $('[data-fundraiser-drawer-lines]', drawerRoot);
    var emptyEl = $('[data-fundraiser-drawer-empty]', drawerRoot);
    var footerEl = $('[data-fundraiser-drawer-footer]', drawerRoot);
    var subtotalEl = $('[data-fundraiser-drawer-subtotal]', drawerRoot);

    if (!cart || !cart.items || cart.items.length === 0) {
      if (linesEl) linesEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      if (footerEl) footerEl.hidden = true;
      updatePill(0);
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    if (footerEl) footerEl.hidden = false;

    if (linesEl) {
      linesEl.innerHTML = cart.items.map(function (item) {
        var img = item.image
          ? '<img src="' + escapeHtml(item.image.replace(/_(small|compact|medium|large|grande|original)\./, '_200x200.')) + '" alt="" class="fr-drawer__line-img" width="80" height="80" loading="lazy">'
          : '<div class="fr-drawer__line-img fr-drawer__line-img--placeholder" aria-hidden="true"></div>';

        var propsHtml = '';
        if (item.properties) {
          var pkeys = Object.keys(item.properties).filter(function (k) {
            return k.charAt(0) !== '_' && item.properties[k];
          });
          if (pkeys.length > 0) {
            propsHtml = '<ul class="fr-drawer__line-props" role="list">' + pkeys.map(function (k) {
              var val = item.properties[k];
              // Uploaded files come back as CDN URLs — render a tidy link instead of the raw URL.
              var valHtml = /^https?:\/\//.test(val)
                ? '<a href="' + escapeHtml(val) + '" target="_blank" rel="noopener">View file</a>'
                : escapeHtml(val);
              return '<li><span class="fr-drawer__prop-key">' + escapeHtml(k) + ':</span> ' + valHtml + '</li>';
            }).join('') + '</ul>';
          }
        }

        var variantHtml = '';
        if (item.variant_title && item.variant_title !== 'Default Title') {
          variantHtml = '<p class="fr-drawer__line-variant">' + escapeHtml(item.variant_title) + '</p>';
        }

        return '<li class="fr-drawer__line" data-line-key="' + escapeHtml(item.key) + '">'
          + '<div class="fr-drawer__line-media">' + img + '</div>'
          + '<div class="fr-drawer__line-body">'
          + '<p class="fr-drawer__line-title">' + escapeHtml(item.product_title) + '</p>'
          + variantHtml
          + propsHtml
          + '<div class="fr-drawer__line-controls">'
          + '<div class="fr-drawer__qty" role="group" aria-label="Quantity">'
          + '<button type="button" class="fr-drawer__qty-step" data-drawer-step="-1" aria-label="Decrease quantity">−</button>'
          + '<span class="fr-drawer__qty-value">' + item.quantity + '</span>'
          + '<button type="button" class="fr-drawer__qty-step" data-drawer-step="1" aria-label="Increase quantity">+</button>'
          + '</div>'
          + '<button type="button" class="fr-drawer__remove" data-drawer-remove>Remove</button>'
          + '</div>'
          + '</div>'
          + '<div class="fr-drawer__line-price">' + formatMoneyFromCents(item.final_line_price) + '</div>'
          + '</li>';
      }).join('');
    }

    if (subtotalEl) subtotalEl.textContent = formatMoneyFromCents(cart.total_price);
    updatePill(cart.item_count);
  }

  async function refreshDrawerFromCart() {
    try {
      var res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' });
      if (!res.ok) return null;
      var cart = await res.json();
      renderDrawer(cart);
      return cart;
    } catch (err) { return null; }
  }

  // Drawer line item controls (delegate)
  if (drawerRoot) {
    drawerRoot.addEventListener('click', async function (e) {
      var closeBtn = e.target.closest('[data-fundraiser-drawer-close]');
      if (closeBtn) {
        e.preventDefault();
        closeDrawer();
        return;
      }
      var stepBtn = e.target.closest('[data-drawer-step]');
      var removeBtn = e.target.closest('[data-drawer-remove]');
      var lineEl = (stepBtn || removeBtn) ? e.target.closest('[data-line-key]') : null;
      if (!lineEl) return;

      var key = lineEl.getAttribute('data-line-key');
      var qtyEl = $('.fr-drawer__qty-value', lineEl);
      var currentQty = parseInt(qtyEl ? qtyEl.textContent : '1', 10) || 1;
      var nextQty = currentQty;
      if (removeBtn) nextQty = 0;
      else if (stepBtn) nextQty = Math.max(0, currentQty + parseInt(stepBtn.getAttribute('data-drawer-step'), 10));

      try {
        var res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: nextQty }),
          credentials: 'same-origin'
        });
        if (!res.ok) return;
        var cart = await res.json();
        renderDrawer(cart);
      } catch (err) { /* swallow */ }
    });

    // ESC closes drawer
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawerRoot.classList.contains('is-open')) {
        closeDrawer();
      }
    });
  }

  // ---------- floating cart pill ----------
  function ensurePill() {
    var pill = $('[data-fundraiser-pill]');
    if (pill) return pill;
    if (!drawerRoot) return null;
    pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'fr-pill';
    pill.setAttribute('data-fundraiser-pill', '');
    pill.setAttribute('aria-label', 'Open cart');
    pill.hidden = true;
    pill.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>'
      + '<span class="fr-pill__count" data-fundraiser-pill-count>0</span>'
      + '<span class="fr-pill__label">View cart</span>';
    pill.addEventListener('click', function () {
      refreshDrawerFromCart().then(openDrawer);
    });
    document.body.appendChild(pill);
    return pill;
  }

  function updatePill(count) {
    var pill = ensurePill();
    if (!pill) return;
    var countEl = $('[data-fundraiser-pill-count]', pill);
    if (countEl) countEl.textContent = String(count || 0);
    pill.hidden = !(count > 0);
  }

  // ---------- AJAX add-to-cart on fundraiser product pages ----------
  function bindProductForm(form) {
    var submit = $('[data-fundraiser-submit]', form);
    var errorEl = $('[data-fundraiser-error]', form);
    var defaultText = $('[data-submit-default]', submit);
    var loadingText = $('[data-submit-loading]', submit);
    var addedText = $('[data-submit-added]', submit);

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg || 'Could not add to cart. Please try again.';
      errorEl.hidden = false;
    }
    function clearError() { if (errorEl) errorEl.hidden = true; }

    function setState(state) {
      if (defaultText) defaultText.hidden = (state !== 'default');
      if (loadingText) loadingText.hidden = (state !== 'loading');
      if (addedText) addedText.hidden = (state !== 'added');
      if (submit) submit.disabled = (state === 'loading');
    }

    // Show selected filenames under the "Add files" control.
    var fileInput = $('[data-fundraiser-files]', form);
    var fileList = $('[data-fundraiser-file-list]', form);
    if (fileInput && fileList) {
      fileInput.addEventListener('change', function () {
        var files = fileInput.files;
        if (!files || !files.length) { fileList.hidden = true; fileList.textContent = ''; return; }
        var names = Array.prototype.map.call(files, function (f) { return f.name; });
        fileList.textContent = names.length + (names.length === 1 ? ' file' : ' files') + ' selected: ' + names.join(', ');
        fileList.hidden = false;
      });
    }

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      clearError();
      setState('loading');
      try {
        // Make sure the cart attribute is set BEFORE the add (atomic from the buyer's POV).
        await setCartAttribute();

        var formData = new FormData(form);
        // The file input has no `name`, so it isn't auto-serialized. Append each
        // selected file as its own line-item property, since Shopify keeps only
        // one file per property name (properties[Uploaded File 1..N]).
        if (fileInput && fileInput.files && fileInput.files.length) {
          Array.prototype.forEach.call(fileInput.files, function (file, i) {
            formData.append('properties[Uploaded File ' + (i + 1) + ']', file, file.name);
          });
        }
        var addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
          credentials: 'same-origin'
        });
        if (!addRes.ok) {
          var err = await addRes.json().catch(function () { return null; });
          showError(err && err.description ? err.description : null);
          setState('default');
          return;
        }
        setState('added');
        await refreshDrawerFromCart();
        openDrawer();
        setTimeout(function () { setState('default'); }, 1200);
      } catch (err) {
        showError();
        setState('default');
      }
    });
  }

  $$('[data-fundraiser-product-form]').forEach(bindProductForm);

  // ---------- initial cart sync ----------
  // On fundraiser pages: set cart attribute proactively, then refresh drawer state
  // so the pill shows up immediately if there are pre-existing items.
  if ($('[data-fundraiser-product]') || $('[data-fundraiser-stat-strip]') || $('[data-fundraiser-goal]') || document.body.classList.contains('fundraiser-layout')) {
    setCartAttribute().finally(refreshDrawerFromCart);
  }

  // ---------- share modal ----------
  var shareModal = document.querySelector('[data-fr-share-modal]');
  var shareLastTrigger = null;
  var shareCurrentCtx = null;

  function buildPlatformUrl(platform, ctx) {
    var u = encodeURIComponent(ctx.url);
    var t = encodeURIComponent(ctx.text || '');
    var title = encodeURIComponent(ctx.title || '');
    var img = encodeURIComponent(ctx.image || '');
    var bodyWithUrl = encodeURIComponent((ctx.text ? ctx.text + ' ' : '') + ctx.url);
    switch (platform) {
      case 'twitter':   return 'https://twitter.com/intent/tweet?url=' + u + '&text=' + t;
      case 'facebook':  return 'https://www.facebook.com/sharer/sharer.php?u=' + u;
      case 'linkedin':  return 'https://www.linkedin.com/sharing/share-offsite/?url=' + u;
      case 'pinterest': return 'https://pinterest.com/pin/create/button/?url=' + u + '&media=' + img + '&description=' + t;
      case 'whatsapp':  return 'https://wa.me/?text=' + bodyWithUrl;
      case 'email':     return 'mailto:?subject=' + title + '&body=' + bodyWithUrl;
      case 'sms':       return 'sms:?&body=' + bodyWithUrl;
      default:          return '#';
    }
  }

  function renderShareQR(url) {
    if (!shareModal) return;
    var qrEl = shareModal.querySelector('[data-fr-share-qr]');
    if (!qrEl) return;
    qrEl.innerHTML = '';
    if (typeof window.QRCode === 'undefined') return;
    /* eslint-disable no-new */
    new window.QRCode(qrEl, {
      text: url,
      width: 200,
      height: 200,
      colorDark: '#0d0d10',
      colorLight: '#ffffff',
      correctLevel: window.QRCode.CorrectLevel ? window.QRCode.CorrectLevel.M : 1
    });
  }

  function showShareToast(message) {
    if (!shareModal) return;
    var toast = shareModal.querySelector('[data-fr-share-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    toast.setAttribute('data-visible', 'true');
    clearTimeout(showShareToast._t);
    showShareToast._t = setTimeout(function () {
      toast.setAttribute('data-visible', 'false');
      setTimeout(function () { toast.hidden = true; }, 240);
    }, 2200);
  }

  function openShareModal(trigger) {
    if (!shareModal) return;
    shareLastTrigger = trigger;
    var ctx = {
      url:   trigger.getAttribute('data-share-url')   || window.location.href,
      title: trigger.getAttribute('data-share-title') || document.title,
      text:  trigger.getAttribute('data-share-text')  || '',
      image: trigger.getAttribute('data-share-image') || ''
    };
    shareCurrentCtx = ctx;

    // Title — match trigger context (fundraiser vs product)
    var titleEl = shareModal.querySelector('#fr-share-modal-title');
    var triggerKind = trigger.getAttribute('data-fr-share-kind');
    if (titleEl) {
      if (triggerKind === 'product') titleEl.textContent = 'Share this product';
      else titleEl.textContent = 'Share this fundraiser';
    }

    // Platform hrefs
    var platformLinks = shareModal.querySelectorAll('[data-fr-share-platform]');
    platformLinks.forEach(function (el) {
      var p = el.getAttribute('data-fr-share-platform');
      if (p === 'instagram') return; // copy-link affordance, no href
      var built = buildPlatformUrl(p, ctx);
      el.setAttribute('href', built);
    });

    // Copy-link input
    var urlInput = shareModal.querySelector('[data-fr-share-url-input]');
    if (urlInput) urlInput.value = ctx.url;
    var copyLabel = shareModal.querySelector('[data-fr-share-copy-label]');
    if (copyLabel) copyLabel.textContent = 'Copy link';

    // QR
    renderShareQR(ctx.url);

    // Open
    shareModal.hidden = false;
    shareModal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('fr-share-open');

    // Focus the close button so keyboard users land somewhere sensible
    setTimeout(function () {
      var closeBtn = shareModal.querySelector('[data-fr-share-close]:not([tabindex])');
      if (closeBtn) closeBtn.focus();
    }, 60);
  }

  function closeShareModal() {
    if (!shareModal) return;
    shareModal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('fr-share-open');
    setTimeout(function () { shareModal.hidden = true; }, 200);
    if (shareLastTrigger && typeof shareLastTrigger.focus === 'function') {
      shareLastTrigger.focus();
    }
  }

  function isShareModalOpen() {
    return shareModal && shareModal.getAttribute('aria-hidden') === 'false';
  }

  // Trigger delegation — works for current + future triggers
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-fr-share-trigger]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      openShareModal(trigger);
    }
  });

  if (shareModal) {
    // Close buttons (X + backdrop)
    shareModal.addEventListener('click', function (e) {
      if (e.target.closest('[data-fr-share-close]')) {
        closeShareModal();
      }
    });

    // Esc to close + Tab focus-trap
    document.addEventListener('keydown', function (e) {
      if (!isShareModalOpen()) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeShareModal();
        return;
      }
      if (e.key === 'Tab') {
        var focusables = shareModal.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    // Platform clicks
    shareModal.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-fr-share-platform]');
      if (!btn) return;
      var platform = btn.getAttribute('data-fr-share-platform');

      // Instagram = copy-link affordance, not a real share URL
      if (platform === 'instagram') {
        e.preventDefault();
        if (shareCurrentCtx && navigator.clipboard) {
          navigator.clipboard.writeText(shareCurrentCtx.url).then(function () {
            showShareToast('Link copied — paste in your Instagram bio or story');
          }).catch(function () {
            showShareToast('Couldn\'t copy — long-press the link to copy');
          });
        }
        return;
      }

      // Anchors with target="_blank" open natively; just let them through
      // (popup sizing for X/Facebook/LinkedIn happens here)
      if (btn.tagName === 'A' && btn.getAttribute('target') === '_blank' && platform !== 'whatsapp') {
        e.preventDefault();
        var built = btn.getAttribute('href');
        window.open(built, '_blank', 'noopener,noreferrer,width=600,height=600');
      }
      // mailto / sms / whatsapp: let the browser handle the default href
    });

    // Copy-link button
    var copyBtn = shareModal.querySelector('[data-fr-share-copy]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        if (!shareCurrentCtx) return;
        var label = shareModal.querySelector('[data-fr-share-copy-label]');
        var urlInput = shareModal.querySelector('[data-fr-share-url-input]');
        var doCopy = function () {
          if (label) {
            label.textContent = 'Copied!';
            setTimeout(function () { label.textContent = 'Copy link'; }, 1800);
          }
        };
        if (navigator.clipboard) {
          navigator.clipboard.writeText(shareCurrentCtx.url).then(doCopy).catch(function () {
            if (urlInput) { urlInput.select(); document.execCommand && document.execCommand('copy'); doCopy(); }
          });
        } else if (urlInput) {
          urlInput.select();
          document.execCommand && document.execCommand('copy');
          doCopy();
        }
      });
    }

    // QR download
    var dlBtn = shareModal.querySelector('[data-fr-share-download-qr]');
    if (dlBtn) {
      dlBtn.addEventListener('click', function () {
        if (!shareCurrentCtx) return;
        var qrEl = shareModal.querySelector('[data-fr-share-qr]');
        if (!qrEl) return;
        var canvas = qrEl.querySelector('canvas');
        var img = qrEl.querySelector('img');
        var dataUrl = null;
        if (canvas && canvas.toDataURL) {
          dataUrl = canvas.toDataURL('image/png');
        } else if (img && img.src) {
          dataUrl = img.src;
        }
        if (!dataUrl) return;
        var link = shareModal.querySelector('[data-fr-share-download-link]');
        if (!link) return;
        // Build filename from URL
        var slugMatch = (shareCurrentCtx.url.match(/\/pages\/([^/?#]+)/) || shareCurrentCtx.url.match(/\/products\/([^/?#]+)/));
        var slug = slugMatch ? slugMatch[1] : 'fundraiser';
        link.setAttribute('href', dataUrl);
        link.setAttribute('download', slug + '-qr.png');
        link.click();
      });
    }
  }

  // Expose for theme editor debugging
  window.FundraiserDrawer = { open: openDrawer, close: closeDrawer, refresh: refreshDrawerFromCart };
  window.FundraiserShare = { open: openShareModal, close: closeShareModal };
})();
